import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next";
import {
  AnalyticsContentProperties,
  SetAnalyticsDataForContent,
  apiTrackerToAnalyticsContentProperties,
} from "~/components/Analytics";
import { postsToContentPromoProps } from "~/components/ContentPromo/postToContentPromoProps";
import { PageMeta, PostMetadata } from "~/components/PageMeta";
import { apiPostV2ToPageMetadata } from "~/components/PageMeta/PageMeta";
import { apiToPostEditCTAProps } from "~/components/PostEditCTA/apiToPostEditCTAProps";
import { TrackerPage, TrackerPageProps } from "~/components/TrackerPage";
import {
  fetchTrackers,
  fetchTrackersPreview,
} from "~/lib/server/asgardApiCalls";
import { createGetStaticPathsForPosts } from "~/lib/server/createGetStaticPathsForPosts";
import { trackerToTrackerPageProps } from "~/lib/server/trackerToTrackerPageProps";
import { wrapGetStaticProps } from "~/lib/server/wrapGetProps";
import { getValueOrDefault } from "~/lib/shared/getValueOrDefault";
import { PostTypeEnum } from "~/types/asgard/PostType";

export type PageParams = {
  trackerParam: string;
};

export const getStaticPaths = createGetStaticPathsForPosts(
  PostTypeEnum.Tracker,
  PostTypeEnum.Tracker,
  "slug"
);

type StaticProps = TrackerPageProps & {
  meta: PostMetadata;
  analytics: AnalyticsContentProperties;
};

export const getStaticProps: GetStaticProps<StaticProps, PageParams> =
  wrapGetStaticProps(async ({ params, preview }) => {
    const { trackerParam } = params!;
    const fetchMethod = preview ? fetchTrackersPreview : fetchTrackers;
    const { data: tracker } = await fetchMethod(trackerParam!);

    const postEditCTAProps = await apiToPostEditCTAProps({
      isPreview: preview,
      postId: tracker.id,
      postParam: trackerParam,
    });

    return {
      props: {
        ...trackerToTrackerPageProps(tracker),
        enableSidecar: true,
        postEditCTAProps,
        meta: apiPostV2ToPageMetadata(tracker),
        contentPromoProps: postsToContentPromoProps(tracker.relatedStories),
        analytics: apiTrackerToAnalyticsContentProperties(tracker),
        preview: getValueOrDefault(preview, false),
      } satisfies StaticProps,
    };
  });

const TrackersPage: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ analytics, meta, ...props }) => (
  <>
    <SetAnalyticsDataForContent {...analytics} />
    <PageMeta {...meta} />
    <TrackerPage {...props} />
  </>
);

export default TrackersPage;
